import React, { useState, useEffect, useMemo } from 'react';
import { useCurrency } from '../utils/CurrencyContext';
import { useTheme } from '../utils/ThemeContext';
import { useLanguage } from '../utils/LanguageContext';
import { useToast } from './ToastContext';
import { usePermissions } from '../utils/PermissionContext';
import { get, put, post, del } from '../utils/api';
import {
  FaStore,
  FaTruck,
  FaLayerGroup,
  FaPercent,
  FaBoxes,
  FaSlidersH,
  FaSave,
  FaPlus,
  FaTrash,
  FaSearch,
  FaShieldAlt,
  FaInfoCircle,
  FaLock,
  FaCheckCircle,
  FaTag,
  FaTimes
} from 'react-icons/fa';
import './Settings.css';

const DEFAULT_DISPENSING_UNITS = [
  'piece',
  'tablet',
  'capsule',
  'bottle',
  'strip',
  'box',
  'tube',
  'vial',
  'sachet',
  'ampoule',
  'drops',
  'syrup'
];

const GST_SLABS = [0, 5, 12, 18, 28];

const Settings = () => {
  const { currencySymbol, setCurrencySymbol } = useCurrency();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { showToast } = useToast();
  const { userRole, loading: permissionsLoading } = usePermissions();

  const [activeTab, setActiveTab] = useState('store');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Tab 1: Store & Pharmacy Profile
  const [storeName, setStoreName] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [storeEmail, setStoreEmail] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [dlNumber, setDlNumber] = useState('');
  const [gstin, setGstin] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('INV-');
  const [storeTagline, setStoreTagline] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('50');
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState('499');

  // Tab 2: Medicine Categories Master
  const [categories, setCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categorySubmitting, setCategorySubmitting] = useState(false);

  // Tab 3: Tax & HSN Master
  const [defaultHsnCode, setDefaultHsnCode] = useState('3004');
  const [defaultGstRate, setDefaultGstRate] = useState('12');
  const [taxScheme, setTaxScheme] = useState('regular');

  // Tab 4: Unit of Measurement & Packaging
  const [dispensingUnits, setDispensingUnits] = useState(DEFAULT_DISPENSING_UNITS);
  const [newUnitInput, setNewUnitInput] = useState('');
  const [defaultStripConversion, setDefaultStripConversion] = useState('10');
  const [defaultBoxConversion, setDefaultBoxConversion] = useState('100');

  // Tab 5: Inventory & System Preferences
  const [lowStockThreshold, setLowStockThreshold] = useState('10');
  const [expiryAlertDays, setExpiryAlertDays] = useState('60');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);

  // Load all settings & categories from backend & localStorage
  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      // Fetch both settings and categories in parallel for maximum speed
      const [settingsRes, catRes] = await Promise.all([
        get('/settings').catch((e) => {
          console.warn('Settings fetch error:', e);
          return null;
        }),
        get('/medicines/categories').catch((e) => {
          console.warn('Categories fetch error:', e);
          return null;
        })
      ]);

      if (settingsRes && settingsRes.success && settingsRes.data) {
        const s = settingsRes.data;
        setStoreName(s.STORE_NAME || localStorage.getItem('storeName') || 'Medicin Pharmacy');
        setStorePhone(s.HELPLINE_PHONE || s.STORE_PHONE || localStorage.getItem('storePhone') || '1800-MEDICIN');
        setStoreEmail(s.HELPLINE_EMAIL || s.STORE_EMAIL || localStorage.getItem('storeEmail') || 'support@medicin.com');
        setStoreAddress(s.STORE_ADDRESS || localStorage.getItem('storeAddress') || 'G-27, 2nd Floor, Sector 4, Kolkata - 700091, West Bengal, India');
        setDlNumber(s.REGISTRATION_NUMBER || s.DL_NUMBER || localStorage.getItem('storeDlNumber') || 'DL-2024-WB-09881');
        setGstin(s.GSTIN || localStorage.getItem('storeGstin') || '19AAACM1234F1Z5');
        setInvoicePrefix(s.INVOICE_PREFIX || localStorage.getItem('invoicePrefix') || 'INV-');
        setStoreTagline(s.STORE_TAGLINE || localStorage.getItem('storeTagline') || 'Your Trusted Healthcare & Pharmacy Partner');
        setDeliveryCharge(s.DELIVERY_CHARGE !== undefined ? String(s.DELIVERY_CHARGE) : (localStorage.getItem('deliveryCharge') || '50'));
        setFreeDeliveryThreshold(s.FREE_DELIVERY_THRESHOLD !== undefined ? String(s.FREE_DELIVERY_THRESHOLD) : (localStorage.getItem('freeDeliveryThreshold') || '499'));

        setDefaultHsnCode(s.DEFAULT_HSN_CODE || localStorage.getItem('defaultHsnCode') || '3004');
        setDefaultGstRate(s.DEFAULT_GST_RATE || localStorage.getItem('defaultGstRate') || '12');
        setTaxScheme(s.TAX_SCHEME || localStorage.getItem('taxScheme') || 'regular');

        if (s.DISPENSING_UNITS) {
          try {
            const parsed = JSON.parse(s.DISPENSING_UNITS);
            if (Array.isArray(parsed) && parsed.length > 0) setDispensingUnits(parsed);
          } catch (e) {
            // keep default
          }
        }
        setDefaultStripConversion(s.DEFAULT_STRIP_CONVERSION || localStorage.getItem('defaultStripConversion') || '10');
        setDefaultBoxConversion(s.DEFAULT_BOX_CONVERSION || localStorage.getItem('defaultBoxConversion') || '100');

        setLowStockThreshold(s.LOW_STOCK_THRESHOLD || localStorage.getItem('lowStockThreshold') || '10');
        setExpiryAlertDays(s.EXPIRY_ALERT_DAYS || localStorage.getItem('expiryAlertDays') || '60');
        
        if (s.CURRENCY_SYMBOL) {
          setCurrencySymbol(s.CURRENCY_SYMBOL);
        }
      } else {
        // Fallback to local storage
        setStoreName(localStorage.getItem('storeName') || 'MEDICIN HEALTH PHARMACY');
        setStorePhone(localStorage.getItem('storePhone') || '+91 98765 43210');
        setStoreEmail(localStorage.getItem('storeEmail') || 'support@medicin.com');
        setStoreAddress(localStorage.getItem('storeAddress') || 'Oberoi Gardens, D Wing, Andheri East, Mumbai');
        setDlNumber(localStorage.getItem('storeDlNumber') || 'MH-MZ2-20B-123456');
        setGstin(localStorage.getItem('storeGstin') || '27AAAAA0000A1Z5');
        setInvoicePrefix(localStorage.getItem('invoicePrefix') || 'INV-');
        setStoreTagline(localStorage.getItem('storeTagline') || 'Your Trusted Healthcare & Pharmacy Partner');
      }

      if (catRes && catRes.success && Array.isArray(catRes.data)) {
        setCategories(catRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch settings / categories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllSettings();
  }, []);

  // Save Settings Handler
  const handleSaveSettings = async (sectionName = 'Settings') => {
    setSaving(true);
    const payload = {
      STORE_NAME: storeName,
      STORE_PHONE: storePhone,
      HELPLINE_PHONE: storePhone,
      STORE_EMAIL: storeEmail,
      HELPLINE_EMAIL: storeEmail,
      STORE_ADDRESS: storeAddress,
      DL_NUMBER: dlNumber,
      REGISTRATION_NUMBER: dlNumber,
      GSTIN: gstin,
      INVOICE_PREFIX: invoicePrefix,
      STORE_TAGLINE: storeTagline,
      DELIVERY_CHARGE: deliveryCharge,
      FREE_DELIVERY_THRESHOLD: freeDeliveryThreshold,
      DEFAULT_HSN_CODE: defaultHsnCode,
      DEFAULT_GST_RATE: defaultGstRate,
      TAX_SCHEME: taxScheme,
      DISPENSING_UNITS: JSON.stringify(dispensingUnits),
      DEFAULT_STRIP_CONVERSION: defaultStripConversion,
      DEFAULT_BOX_CONVERSION: defaultBoxConversion,
      LOW_STOCK_THRESHOLD: lowStockThreshold,
      EXPIRY_ALERT_DAYS: expiryAlertDays,
      CURRENCY_SYMBOL: currencySymbol
    };

    // Save to LocalStorage for instant access
    localStorage.setItem('storeName', storeName || '');
    localStorage.setItem('storePhone', storePhone || '');
    localStorage.setItem('storeEmail', storeEmail || '');
    localStorage.setItem('storeAddress', storeAddress || '');
    localStorage.setItem('storeDlNumber', dlNumber || '');
    localStorage.setItem('storeGstin', gstin || '');
    localStorage.setItem('invoicePrefix', invoicePrefix || 'INV-');
    localStorage.setItem('storeTagline', storeTagline || '');
    localStorage.setItem('deliveryCharge', deliveryCharge || '50');
    localStorage.setItem('freeDeliveryThreshold', freeDeliveryThreshold || '499');
    localStorage.setItem('defaultHsnCode', defaultHsnCode || '3004');
    localStorage.setItem('defaultGstRate', defaultGstRate || '12');
    localStorage.setItem('taxScheme', taxScheme || 'regular');
    localStorage.setItem('defaultStripConversion', defaultStripConversion || '10');
    localStorage.setItem('defaultBoxConversion', defaultBoxConversion || '100');
    localStorage.setItem('lowStockThreshold', lowStockThreshold || '10');
    localStorage.setItem('expiryAlertDays', expiryAlertDays || '60');

    try {
      const res = await put('/settings', payload);
      if (res && res.success) {
        showToast(`${sectionName} saved successfully!`, 'success');
      } else {
        showToast(`${sectionName} saved locally.`, 'info');
      }
    } catch (err) {
      console.warn('Backend settings update warning:', err);
      showToast(`${sectionName} saved locally (offline mode).`, 'success');
    } finally {
      setSaving(false);
    }
  };

  // Category Master: Add Category
  const handleAddCategory = async (e) => {
    if (e) e.preventDefault();
    if (!newCategoryName || !newCategoryName.trim()) {
      showToast('Please enter a valid category name', 'error');
      return;
    }

    const trimmed = newCategoryName.trim();
    if (categories.some((c) => c.toLowerCase() === trimmed.toLowerCase())) {
      showToast(`Category "${trimmed}" already exists`, 'error');
      return;
    }

    setCategorySubmitting(true);
    try {
      const res = await post('/medicines/categories', { name: trimmed });
      if (res && res.success) {
        showToast(`Category "${trimmed}" added successfully!`, 'success');
        setCategories((prev) => [trimmed, ...prev]);
        setNewCategoryName('');
      } else {
        showToast(res?.message || 'Failed to add category', 'error');
      }
    } catch (err) {
      console.error('Error adding category:', err);
      showToast(err.message || 'Error adding category', 'error');
    } finally {
      setCategorySubmitting(false);
    }
  };

  // Category Master: Delete Category
  const handleDeleteCategory = async (catName) => {
    if (!window.confirm(`Are you sure you want to delete category "${catName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const res = await del(`/medicines/categories/${encodeURIComponent(catName)}`);
      if (res && res.success) {
        showToast(`Category "${catName}" deleted successfully`, 'success');
        setCategories((prev) => prev.filter((c) => c !== catName));
      } else {
        showToast(res?.message || 'Cannot delete category', 'error');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      showToast(err.message || 'Error deleting category', 'error');
    }
  };

  // Unit of Measurement: Add Custom Unit
  const handleAddUnit = () => {
    if (!newUnitInput || !newUnitInput.trim()) return;
    const cleanUnit = newUnitInput.trim().toLowerCase();
    if (dispensingUnits.includes(cleanUnit)) {
      showToast(`Unit "${cleanUnit}" is already listed`, 'warning');
      return;
    }
    setDispensingUnits((prev) => [...prev, cleanUnit]);
    setNewUnitInput('');
    showToast(`Added unit "${cleanUnit}". Click "Save Packaging Defaults" to apply.`, 'info');
  };

  // Unit of Measurement: Remove Unit
  const handleRemoveUnit = (unitToRemove) => {
    if (dispensingUnits.length <= 1) {
      showToast('At least one dispensing unit is required.', 'error');
      return;
    }
    setDispensingUnits((prev) => prev.filter((u) => u !== unitToRemove));
    showToast(`Removed unit "${unitToRemove}". Click "Save Packaging Defaults" to apply.`, 'info');
  };

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    const q = categorySearch.toLowerCase();
    return categories.filter((c) => c.toLowerCase().includes(q));
  }, [categories, categorySearch]);

  // Strict RBAC Guard: If user is not admin, show restricted barrier
  if (!permissionsLoading && userRole && userRole !== 'admin') {
    return (
      <div className="settings-page">
        <div className="settings-card access-denied-card text-center p-5">
          <div className="access-denied-icon mb-3">
            <FaLock size={48} className="text-danger" />
          </div>
          <h3 className="text-danger fw-bold">Access Denied</h3>
          <p className="text-muted fs-5 mt-2">
            You do not have administrative permissions to view or edit Master Data Settings.
          </p>
          <p className="text-secondary small">
            Only users with the <span className="badge bg-danger">admin</span> role have access to this portal.
          </p>
          <div className="mt-4">
            <a href="/dashboard" className="btn btn-primary px-4 py-2">
              Return to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Header Banner */}
      <div className="settings-header-banner">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1 rounded-pill">
                <FaShieldAlt className="me-1" /> Admin Master Portal
              </span>
              <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1 rounded-pill">
                <FaCheckCircle className="me-1" /> RBAC Protected
              </span>
            </div>
            <h1 className="settings-main-title mt-2">Settings & Master Data Management</h1>
            <p className="settings-main-subtitle text-muted mb-0">
              Configure pharmacy credentials, medicine category master, tax slabs, units of measurement, and system thresholds.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 shadow-sm"
              onClick={() => handleSaveSettings('All Master Data')}
              disabled={saving}
            >
              <FaSave />
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="settings-nav-tabs">
        <button
          type="button"
          className={`settings-tab-btn ${activeTab === 'store' ? 'active' : ''}`}
          onClick={() => setActiveTab('store')}
        >
          <FaStore className="tab-icon" />
          <span>Store & License Master</span>
        </button>

        <button
          type="button"
          className={`settings-tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          <FaLayerGroup className="tab-icon" />
          <span>Drug Categories Master ({categories.length})</span>
        </button>

        <button
          type="button"
          className={`settings-tab-btn ${activeTab === 'tax' ? 'active' : ''}`}
          onClick={() => setActiveTab('tax')}
        >
          <FaPercent className="tab-icon" />
          <span>Tax & HSN Master</span>
        </button>

        <button
          type="button"
          className={`settings-tab-btn ${activeTab === 'packaging' ? 'active' : ''}`}
          onClick={() => setActiveTab('packaging')}
        >
          <FaBoxes className="tab-icon" />
          <span>Units & Packaging Master</span>
        </button>

        <button
          type="button"
          className={`settings-tab-btn ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          <FaSlidersH className="tab-icon" />
          <span>Inventory & Preferences</span>
        </button>
      </div>

      {/* Tab 1: Store & License Master */}
      {activeTab === 'store' && (
        <div className="settings-card">
          <div className="settings-card-header">
            <div>
              <h4 className="settings-card-title">Pharmacy & Legal Credentials</h4>
              <p className="settings-card-desc">
                These credentials appear on customer bills, GST invoices, purchase orders, and official receipts.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => handleSaveSettings('Store Credentials')}
              disabled={saving}
            >
              <FaSave /> Save Credentials
            </button>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <label htmlFor="storeNameInput" className="form-label fw-semibold">
                Pharmacy / Store Legal Name <span className="text-danger">*</span>
              </label>
              <input
                id="storeNameInput"
                type="text"
                className="form-control form-control-lg"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder="e.g., Medicin Health Pharmacy"
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="storeTaglineInput" className="form-label fw-semibold">
                Store Tagline / Slogan
              </label>
              <input
                id="storeTaglineInput"
                type="text"
                className="form-control form-control-lg"
                value={storeTagline}
                onChange={(e) => setStoreTagline(e.target.value)}
                placeholder="e.g., Serving Health With Trust"
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="dlNumberInput" className="form-label fw-semibold">
                Pharmacy Registration / Drug License (DL) Number <span className="text-danger">*</span>
              </label>
              <input
                id="dlNumberInput"
                type="text"
                className="form-control"
                value={dlNumber}
                onChange={(e) => setDlNumber(e.target.value)}
                placeholder="e.g., DL-2024-WB-09881"
              />
              <small className="text-muted">Mandatory registration number; displayed on customer website footer & invoices</small>
            </div>

            <div className="col-md-6">
              <label htmlFor="gstinInput" className="form-label fw-semibold">
                GSTIN / Tax Identification Number <span className="text-danger">*</span>
              </label>
              <input
                id="gstinInput"
                type="text"
                className="form-control"
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="e.g., 19AAACM1234F1Z5"
              />
              <small className="text-muted">15-digit GSTIN displayed on customer website footer & tax invoices</small>
            </div>

            <div className="col-md-4">
              <label htmlFor="storePhoneInput" className="form-label fw-semibold">
                Customer Helpline Number <span className="text-danger">*</span>
              </label>
              <input
                id="storePhoneInput"
                type="text"
                className="form-control"
                value={storePhone}
                onChange={(e) => setStorePhone(e.target.value)}
                placeholder="e.g., 1800-MEDICIN or +91 98765 43210"
              />
              <small className="text-muted">Displayed on customer website top bar, header & help center</small>
            </div>

            <div className="col-md-4">
              <label htmlFor="storeEmailInput" className="form-label fw-semibold">
                Customer Support Email <span className="text-danger">*</span>
              </label>
              <input
                id="storeEmailInput"
                type="email"
                className="form-control"
                value={storeEmail}
                onChange={(e) => setStoreEmail(e.target.value)}
                placeholder="e.g., support@medicin.com"
              />
              <small className="text-muted">Displayed on customer website footer & support portal</small>
            </div>

            <div className="col-md-4">
              <label htmlFor="invoicePrefixInput" className="form-label fw-semibold">
                Invoice Number Prefix
              </label>
              <input
                id="invoicePrefixInput"
                type="text"
                className="form-control"
                value={invoicePrefix}
                onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase())}
                placeholder="e.g., INV-, BILL-"
              />
              <small className="text-muted">Prepended to all auto-generated bills</small>
            </div>

            <div className="col-12">
              <label htmlFor="storeAddressInput" className="form-label fw-semibold">
                Registered Physical Address <span className="text-danger">*</span>
              </label>
              <textarea
                id="storeAddressInput"
                rows="3"
                className="form-control"
                value={storeAddress}
                onChange={(e) => setStoreAddress(e.target.value)}
                placeholder="Shop No. 4, Ground Floor, Healthcare Arcade, MG Road, Mumbai - 400001"
              />
            </div>

            {/* Delivery Charge & Free Delivery Configuration */}
            <div className="col-12 mt-4 pt-3 border-top">
              <h5 className="fw-bold text-dark d-flex align-items-center gap-2 mb-1">
                <FaTruck className="text-primary" /> Delivery & Shipping Charges Configuration
              </h5>
              <p className="text-muted small mb-3">
                Configure standard customer delivery fees and the threshold for free doorstep delivery.
              </p>
            </div>

            <div className="col-md-6">
              <label htmlFor="deliveryChargeInput" className="form-label fw-semibold">
                Standard Delivery Charge ({currencySymbol}) <span className="text-danger">*</span>
              </label>
              <input
                id="deliveryChargeInput"
                type="number"
                min="0"
                step="1"
                className="form-control form-control-lg"
                value={deliveryCharge}
                onChange={(e) => setDeliveryCharge(e.target.value)}
                placeholder="e.g., 50"
              />
              <small className="text-muted">Fee applied to customer orders below the free delivery limit (Set to 0 for free delivery on all orders).</small>
            </div>

            <div className="col-md-6">
              <label htmlFor="freeDeliveryThresholdInput" className="form-label fw-semibold">
                Free Delivery Minimum Order Value ({currencySymbol}) <span className="text-danger">*</span>
              </label>
              <input
                id="freeDeliveryThresholdInput"
                type="number"
                min="0"
                step="1"
                className="form-control form-control-lg"
                value={freeDeliveryThreshold}
                onChange={(e) => setFreeDeliveryThreshold(e.target.value)}
                placeholder="e.g., 499"
              />
              <small className="text-muted">Orders with items subtotal equal to or exceeding this amount receive free delivery (e.g. ₹499).</small>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Drug Categories Master */}
      {activeTab === 'categories' && (
        <div className="settings-card">
          <div className="settings-card-header">
            <div>
              <h4 className="settings-card-title">Medicine Categories Master</h4>
              <p className="settings-card-desc">
                Manage global therapeutic categories used for medicine categorization, batch indexing, and inventory filters.
              </p>
            </div>
          </div>

          {/* Add Category Form */}
          <form onSubmit={handleAddCategory} className="category-add-form mb-4 p-3 rounded border">
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
              <FaPlus className="text-primary" /> Add New Category
            </h6>
            <div className="row g-2 align-items-center">
              <div className="col-md-8">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name (e.g. Dermatologicals, Ophthalmic, Oncology)..."
                  disabled={categorySubmitting}
                />
              </div>
              <div className="col-md-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                  disabled={categorySubmitting || !newCategoryName.trim()}
                >
                  <FaPlus />
                  {categorySubmitting ? 'Adding...' : 'Add Category'}
                </button>
              </div>
            </div>
          </form>

          {/* Search & Stats Bar */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="search-box-wrapper position-relative flex-grow-1" style={{ maxWidth: '400px' }}>
              <FaSearch className="search-icon position-absolute text-muted" style={{ left: '12px', top: '12px' }} />
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />
            </div>
            <span className="badge bg-secondary-subtle text-secondary px-3 py-2 fs-6">
              Total Categories: <strong>{categories.length}</strong>
            </span>
          </div>

          {/* Categories Grid List */}
          <div className="categories-grid">
            {filteredCategories.length === 0 ? (
              <div className="col-12 text-center py-5 text-muted">
                <FaInfoCircle size={32} className="mb-2 text-secondary" />
                <p className="mb-0">No categories matching "{categorySearch}"</p>
              </div>
            ) : (
              filteredCategories.map((cat, idx) => (
                <div key={idx} className="category-chip-item d-flex align-items-center justify-content-between p-3 rounded border">
                  <div className="d-flex align-items-center gap-2">
                    <FaTag className="text-primary" />
                    <span className="fw-semibold text-break">{cat}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger category-delete-btn"
                    title={`Delete category ${cat}`}
                    onClick={() => handleDeleteCategory(cat)}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tab 3: Tax & HSN Master */}
      {activeTab === 'tax' && (
        <div className="settings-card">
          <div className="settings-card-header">
            <div>
              <h4 className="settings-card-title">Taxation & HSN Master Defaults</h4>
              <p className="settings-card-desc">
                Set default HSN codes and GST slabs pre-filled when adding new medicines or purchase orders.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => handleSaveSettings('Taxation Settings')}
              disabled={saving}
            >
              <FaSave /> Save Tax Settings
            </button>
          </div>

          <div className="row g-4 mt-1">
            <div className="col-md-6">
              <label htmlFor="defaultHsnInput" className="form-label fw-semibold">
                Default Pharmaceutical HSN Code
              </label>
              <input
                id="defaultHsnInput"
                type="text"
                className="form-control form-control-lg"
                value={defaultHsnCode}
                onChange={(e) => setDefaultHsnCode(e.target.value)}
                placeholder="3004"
              />
              <small className="text-muted">
                Chapter 30 HSN <strong>3004</strong> represents medicaments consisting of mixed or unmixed products for therapeutic/prophylactic uses.
              </small>
            </div>

            <div className="col-md-6">
              <label htmlFor="defaultGstRateSelect" className="form-label fw-semibold">
                Default GST Slab for New Medicines (%)
              </label>
              <select
                id="defaultGstRateSelect"
                className="form-select form-select-lg"
                value={defaultGstRate}
                onChange={(e) => setDefaultGstRate(e.target.value)}
              >
                {GST_SLABS.map((slab) => (
                  <option key={slab} value={slab}>
                    {slab}% GST ({slab / 2}% CGST + {slab / 2}% SGST)
                  </option>
                ))}
              </select>
              <small className="text-muted">Default rate applied during stock intake and POS billing.</small>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Tax Compliance Scheme</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="taxScheme"
                    id="schemeRegular"
                    value="regular"
                    checked={taxScheme === 'regular'}
                    onChange={() => setTaxScheme('regular')}
                  />
                  <label className="form-check-label fw-medium" htmlFor="schemeRegular">
                    Regular Scheme (ITC & Tax Split)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="taxScheme"
                    id="schemeComposition"
                    value="composition"
                    checked={taxScheme === 'composition'}
                    onChange={() => setTaxScheme('composition')}
                  />
                  <label className="form-check-label fw-medium" htmlFor="schemeComposition">
                    Composition Scheme (Flat Rate)
                  </label>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="alert alert-info d-flex align-items-start gap-3 mb-0">
                <FaInfoCircle size={22} className="flex-shrink-0 mt-1" />
                <div>
                  <h6 className="alert-heading fw-bold mb-1">GST Splitting Mechanism</h6>
                  <p className="mb-0 small">
                    For intra-state sales within the same state, the GST rate is automatically split into equal parts of <strong>CGST</strong> and <strong>SGST</strong>. For inter-state transactions, full <strong>IGST</strong> is recorded according to statutory GST council norms.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Units & Packaging Master */}
      {activeTab === 'packaging' && (
        <div className="settings-card">
          <div className="settings-card-header">
            <div>
              <h4 className="settings-card-title">Units of Measurement (UOM) & Packaging Multipliers</h4>
              <p className="settings-card-desc">
                Define the standard base dispensing units and default multipliers for strip and box conversions.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => handleSaveSettings('Packaging Defaults')}
              disabled={saving}
            >
              <FaSave /> Save Packaging Defaults
            </button>
          </div>

          <div className="row g-4 mt-1">
            {/* Multipliers */}
            <div className="col-md-6">
              <label htmlFor="stripMultiplierInput" className="form-label fw-semibold">
                Default Strip Conversion (Units per Strip)
              </label>
              <input
                id="stripMultiplierInput"
                type="number"
                min="1"
                className="form-control form-control-lg"
                value={defaultStripConversion}
                onChange={(e) => setDefaultStripConversion(e.target.value)}
                placeholder="10"
              />
              <small className="text-muted">Standard blister/foil packaging count (typically 10 or 15 tablets/capsules per strip).</small>
            </div>

            <div className="col-md-6">
              <label htmlFor="boxMultiplierInput" className="form-label fw-semibold">
                Default Box Conversion (Units per Box)
              </label>
              <input
                id="boxMultiplierInput"
                type="number"
                min="1"
                className="form-control form-control-lg"
                value={defaultBoxConversion}
                onChange={(e) => setDefaultBoxConversion(e.target.value)}
                placeholder="100"
              />
              <small className="text-muted">Standard master carton packaging count (e.g. 10 strips × 10 units = 100 units).</small>
            </div>

            {/* Dispensing Units List */}
            <div className="col-12">
              <label className="form-label fw-semibold">Active Base Dispensing Units</label>
              <p className="text-muted small">These units appear in medicine addition, inventory intake, and dosage instructions.</p>

              <div className="dispensing-units-container">
                {dispensingUnits.map((u, idx) => (
                  <div key={idx} className="dispensing-unit-pill">
                    <span className="dispensing-unit-name">{u}</span>
                    <button
                      type="button"
                      className="dispensing-unit-remove"
                      aria-label={`Remove unit ${u}`}
                      title={`Remove unit ${u}`}
                      onClick={() => handleRemoveUnit(u)}
                    >
                      <FaTimes className="unit-cross-icon" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Custom Unit */}
              <div className="input-group" style={{ maxWidth: '380px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Add custom unit (e.g., puff, patch, kit)..."
                  value={newUnitInput}
                  onChange={(e) => setNewUnitInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddUnit();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center gap-1"
                  onClick={handleAddUnit}
                  disabled={!newUnitInput.trim()}
                >
                  <FaPlus /> Add Unit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 5: Inventory & System Preferences */}
      {activeTab === 'system' && (
        <div className="settings-card">
          <div className="settings-card-header">
            <div>
              <h4 className="settings-card-title">Inventory Alerts & System Preferences</h4>
              <p className="settings-card-desc">
                Set global reorder levels, expiry warning thresholds, currency formatting, and UI theme.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => handleSaveSettings('System Preferences')}
              disabled={saving}
            >
              <FaSave /> Save Preferences
            </button>
          </div>

          <div className="row g-4 mt-1">
            <div className="col-md-6">
              <label htmlFor="lowStockInput" className="form-label fw-semibold">
                Low-Stock Alert Reorder Point (Units)
              </label>
              <input
                id="lowStockInput"
                type="number"
                min="1"
                className="form-control form-control-lg"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value)}
                placeholder="10"
              />
              <small className="text-muted">Triggers low-stock badges when total inventory units drop below this quantity.</small>
            </div>

            <div className="col-md-6">
              <label htmlFor="expiryAlertInput" className="form-label fw-semibold">
                Expiry Alert Lead Time (Days)
              </label>
              <input
                id="expiryAlertInput"
                type="number"
                min="7"
                className="form-control form-control-lg"
                value={expiryAlertDays}
                onChange={(e) => setExpiryAlertDays(e.target.value)}
                placeholder="60"
              />
              <small className="text-muted">Flags batches nearing expiry in inventory and POS alerts.</small>
            </div>

            <div className="col-md-4">
              <label htmlFor="currencySelect" className="form-label fw-semibold">
                Global Currency Symbol
              </label>
              <select
                id="currencySelect"
                className="form-select form-select-lg"
                value={currencySymbol}
                onChange={(e) => setCurrencySymbol(e.target.value)}
              >
                <option value="₹">₹ (INR - Indian Rupee)</option>
                <option value="$">$ (USD - US Dollar)</option>
                <option value="€">€ (EUR - Euro)</option>
                <option value="£">£ (GBP - British Pound)</option>
                <option value="৳">৳ (BDT - Bangladeshi Taka)</option>
                <option value="AED">AED (UAE Dirham)</option>
              </select>
            </div>

            <div className="col-md-4">
              <label htmlFor="themeSelect" className="form-label fw-semibold">
                Application Theme
              </label>
              <select
                id="themeSelect"
                className="form-select form-select-lg"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
              >
                <option value="light">Light Theme (Clean White & Slate)</option>
                <option value="dark">Dark Theme (High Contrast Midnight)</option>
              </select>
            </div>

            <div className="col-md-4">
              <label htmlFor="languageSelect" className="form-label fw-semibold">
                Interface Language
              </label>
              <select
                id="languageSelect"
                className="form-select form-select-lg"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="en">English (Default)</option>
                <option value="hi">Hindi (हिंदी)</option>
                <option value="bn">Bengali (বাংলা)</option>
              </select>
            </div>

            <div className="col-12">
              <h6 className="fw-semibold mt-2 mb-2">Automated Notifications</h6>
              <div className="d-flex flex-wrap gap-4">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="emailNotificationSwitch"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                  />
                  <label className="form-check-label fw-medium" htmlFor="emailNotificationSwitch">
                    Email Notifications for Low Stock & Reorders
                  </label>
                </div>

                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="smsNotificationSwitch"
                    checked={smsNotifications}
                    onChange={(e) => setSmsNotifications(e.target.checked)}
                  />
                  <label className="form-check-label fw-medium" htmlFor="smsNotificationSwitch">
                    SMS Alerts for Urgent Shortages
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;